import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Check, Star, BookOpen, Shield, Users, Palette, Smartphone, Lock, DollarSign, Sparkles, Gift, Wallet } from 'lucide-react';

const li = { display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginBottom: '1rem', color: '#1a365d', fontSize: '0.95rem' };
const ico = { color: '#14b8a6', marginTop: '2px' };
const FEATURES = [
  [Check, 'pricing-feature-unlimited', <><strong>Publicações ilimitadas:</strong> disponibilize livros e periódicos em PDF.</>],
  [Sparkles, 'pricing-ai-highlight', <><strong>Catalogação automática com IA:</strong> simplifique a inclusão e a organização das publicações.</>],
  [Shield, 'pricing-feature-antipiracy', <><strong>Proteção antipirataria:</strong> recursos para restringir capturas de tela e cópias, conforme o dispositivo.</>],
  [Users, 'pricing-feature-subscribers', <><strong>Gestão de assinantes e acessos:</strong> acompanhe e administre seu público em um só lugar.</>],
  [Wallet, 'pricing-feature-finance', <><strong>Gestão financeira automatizada:</strong> a plataforma gerencia todos os pagamentos e a liberação de acesso dos assinantes. Você só define o preço da assinatura e acompanha em tempo real os repasses financeiros mensais para a sua conta.</>],
  [Palette, 'pricing-feature-identity', <><strong>Catálogo com a sua identidade:</strong> personalize seu espaço com a sua logomarca e publique no endereço <span data-testid="pricing-catalog-url" style={{ whiteSpace: 'nowrap' }}>livrum.com.br/<strong style={{ fontWeight: 800, color: '#0d9488' }}>suaeditora</strong></span>.</>],
];

const PricingSection = () => {
  const navigate = useNavigate();

  const handleSelectPlan = () => {
    sessionStorage.setItem('selectedPlan', JSON.stringify({
      id: 'editor_anual',
      title: 'Plano Editora',
      price: 'R$ 3.000',
      libraryType: 'digital'
    }));
    navigate('/cadastro');
  };

  return (
    <section className="pricing-section" id="planos">
      <div className="pricing-container">
        {/* Descrição */}
        <div style={{
          textAlign: 'center',
          marginBottom: '2rem',
          padding: '1.5rem 2rem',
          background: 'rgba(20, 184, 166, 0.1)',
          borderRadius: '16px',
          maxWidth: '800px',
          margin: '0 auto 2.5rem'
        }}>
          <p style={{ color: '#1a365d', margin: 0, fontSize: '1.1rem', lineHeight: '1.7' }}>
            Publique seus <strong style={{ color: '#14b8a6' }}>livros e periódicos</strong> em uma plataforma segura. 
            Defina o valor da assinatura e ofereça uma <strong style={{ color: '#14b8a6' }}>experiência de leitura premium</strong> aos seus assinantes.
          </p>
        </div>

        {/* Card único de plano */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center',
          gap: '2rem',
          flexWrap: 'wrap'
        }}>
          <Card 
            className="pricing-card popular"
            style={{
              position: 'relative',
              border: '2px solid #14b8a6',
              maxWidth: '420px',
              width: '100%'
            }}
          >
            <div style={{
              position: 'absolute',
              top: '-12px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'linear-gradient(135deg, #14b8a6 0%, #5eead4 100%)',
              color: 'white',
              padding: '0.35rem 1.25rem',
              borderRadius: '50px',
              fontSize: '0.8rem',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '0.35rem'
            }}>
              <Star className="w-4 h-4" />
              Plano Único
            </div>
            
            <CardHeader className="text-center" style={{ paddingTop: '2rem' }}>
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #14b8a6 0%, #5eead4 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem'
              }}>
                <BookOpen className="w-8 h-8" style={{ color: 'white' }} />
              </div>
              <CardTitle className="pricing-card-title" style={{ fontSize: '1.5rem' }}>
                Plano Editora
              </CardTitle>
              <p data-testid="pricing-subtitle" style={{ color: '#718096', fontSize: '0.95rem', marginTop: '0.5rem' }}>
                Seu catálogo digital, com publicações ilimitadas.
              </p>
            </CardHeader>
            
            <CardContent className="text-center">
              <div className="pricing-card-price" style={{ marginBottom: '0.5rem' }}>
                <span className="price" style={{ fontSize: '2.5rem', fontWeight: '700', color: '#1a202c' }}>R$ 3.000</span>
                <span className="period" style={{ color: '#718096' }}>/ano</span>
              </div>
              
              <p style={{ 
                color: '#14b8a6', 
                fontWeight: '600', 
                fontSize: '1rem',
                marginBottom: '1.5rem',
                padding: '0.75rem',
                background: 'rgba(20, 184, 166, 0.1)',
                borderRadius: '8px'
              }}>
                + 30% sobre o valor de cada assinatura vendida
              </p>
              <p data-testid="pricing-payment-options" style={{ color: '#1a365d', fontSize: '0.92rem', marginBottom: '1rem', padding: '0.7rem', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px' }}>
                <strong>Pagamento:</strong> à vista no <strong>Pix</strong> ou em até <strong>4x de R$&nbsp;750</strong> no cartão de crédito.
              </p>
              <p data-testid="pricing-trial-highlight" style={{ color: '#92400e', fontWeight: 700, fontSize: '0.95rem', marginBottom: '1rem', padding: '0.7rem', background: '#fef3c7', borderRadius: '8px', display: 'flex', gap: 8, alignItems: 'flex-start', textAlign: 'left' }}>
                <Gift className="w-5 h-5 flex-shrink-0" style={{ marginTop: 1 }} />
                <span>Experimente grátis por 7 dias após o cadastro, sem custo durante o período de teste.</span>
              </p>

              <ul style={{ listStyle: 'none', padding: 0, margin: '1.5rem 0', textAlign: 'left' }}>
                {FEATURES.map(([Icon, testId, text], i) => (
                  <li key={testId} data-testid={testId} style={{ ...li, ...(i === FEATURES.length - 1 ? { marginBottom: 0 } : {}), ...(testId === 'pricing-ai-highlight' ? { background: 'rgba(20, 184, 166, 0.1)', borderRadius: 8, padding: '0.6rem 0.7rem' } : {}) }}>
                    <Icon className="w-5 h-5 flex-shrink-0" style={ico} />
                    <span>{text}</span>
                  </li>
                ))}
              </ul>

              <Button 
                className="btn-primary-custom w-full"
                onClick={handleSelectPlan}
                data-testid="select-plan-btn"
                style={{
                  background: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)',
                  padding: '1rem 2rem',
                  fontSize: '1.1rem',
                  fontWeight: '600'
                }}
              >
                Começar Agora
              </Button>
              <a href="https://livrum.com.br/livrum-catalogo-demonstrativo" target="_blank" rel="noopener noreferrer" data-testid="pricing-demo-catalog-btn"
                style={{ display: 'block', marginTop: '0.75rem', padding: '0.85rem 1.5rem', borderRadius: 8, border: '2px solid #0d9488', color: '#0d9488', fontWeight: 600, textDecoration: 'none', textAlign: 'center' }}>
                Ver catálogo de demonstração
              </a>
              
              <p style={{ 
                marginTop: '1rem', 
                color: '#a0aec0', 
                fontSize: '0.85rem' 
              }}>
                Cancele quando quiser
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Features adicionais */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '1.5rem',
          marginTop: '3rem',
          maxWidth: '900px',
          margin: '3rem auto 0'
        }}>
          <div style={{
            textAlign: 'center',
            padding: '1.5rem',
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
          }}>
            <div style={{ 
              width: '48px', 
              height: '48px', 
              borderRadius: '12px',
              background: 'rgba(20, 184, 166, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 0.75rem'
            }}>
              <Smartphone style={{ width: '24px', height: '24px', color: '#14b8a6' }} />
            </div>
            <h4 style={{ color: '#0f2a4a', marginBottom: '0.5rem', fontWeight: '600' }}>Multiplataforma</h4>
            <p style={{ color: '#1e5f8a', fontSize: '0.9rem', margin: 0 }}>
              Seus leitores acessam de qualquer dispositivo
            </p>
          </div>
          <div style={{
            textAlign: 'center',
            padding: '1.5rem',
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
          }}>
            <div style={{ 
              width: '48px', 
              height: '48px', 
              borderRadius: '12px',
              background: 'rgba(20, 184, 166, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 0.75rem'
            }}>
              <Lock style={{ width: '24px', height: '24px', color: '#14b8a6' }} />
            </div>
            <h4 style={{ color: '#0f2a4a', marginBottom: '0.5rem', fontWeight: '600' }}>Plataforma Segura</h4>
            <p style={{ color: '#1e5f8a', fontSize: '0.9rem', margin: 0 }}>
              Proteção contra pirataria, cópia e captura de tela
            </p>
          </div>
          <div style={{
            textAlign: 'center',
            padding: '1.5rem',
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
          }}>
            <div style={{ 
              width: '48px', 
              height: '48px', 
              borderRadius: '12px',
              background: 'rgba(20, 184, 166, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 0.75rem'
            }}>
              <DollarSign style={{ width: '24px', height: '24px', color: '#14b8a6' }} />
            </div>
            <h4 style={{ color: '#0f2a4a', marginBottom: '0.5rem', fontWeight: '600' }}>Você no Controle</h4>
            <p style={{ color: '#1e5f8a', fontSize: '0.9rem', margin: 0 }}>
              Defina o preço da sua assinatura
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
